import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Alert, App, Button, Collapse, Empty, Input, Modal, Table, Tag, Typography } from 'antd';
import { LoadingOutlined, SafetyOutlined } from '@ant-design/icons';
import { downloadName } from '@lfoc/domain/intake/batch.js';

import { useAccess } from '../../auth/AccessContext.jsx';
import { CenteredLoader } from '../../components/Loader.jsx';
import { downloadedBytes } from '../uploads/uploadFile.js';
import { downloadCsv } from '../../domain/redemptionDocument.js';
import { formatInstant } from '../../format.js';
import { useLiveTopic } from '../../live/LiveContext.js';
import { StepUpCancelled, useStepUp } from '../../stepup/useStepUp.js';
import { AnalyticsRuns } from './AnalyticsRuns.jsx';
import {
    REASON_MAX, analyticsDayWords, chainedRunsOf, fileAnalyticsWords, canDownload, canResendDate, canWaiveWmDate, dayLong, dayText, feedRows, feedStateWords, feedVersions, reasonProblem, resendDates
} from '../../businessDay/businessDay.js';

const { Text } = Typography;

const DAY_COLOUR = Object.freeze({
    DONE: 'green', STALE: 'orange', FAILED: 'red', WAITING_FOR_WM: 'gold', WAITING_EARLIER: 'default', PENDING: 'blue', BUILDING: 'blue', DELIVERING: 'blue'
});
const FILE_COLOUR = Object.freeze({ PICKED_UP: 'green', SENT: 'blue', BUILT: 'blue', EMPTY: 'default', OFF: 'default', FAILED: 'red' });
const titleCase = (value) => String(value ?? '').toLowerCase().replace(/(^|_)(\w)/g, (m, sep, c) => (sep ? ' ' : '') + c.toUpperCase());
const dash = <Text type="secondary">—</Text>;

/** The state of one feed of a date's current version, as a tag; a date not built yet has none. */
const feedTag = (feeds, feed) => {
    const file = (feeds ?? []).find((f) => f.feed === feed);
    return file ? <Tag className="analytics-feed-day-tag" color={FILE_COLOUR[file.state]}>{titleCase(file.state)}</Tag> : dash;
};

/** How a download is saved: the file as the archive holds it, under the analytics name. */
async function saveFeedFile(api, businessDate, row) {
    const answer = await api.books.feedFile(businessDate, row.feed, row.version);
    const bytes = await downloadedBytes(answer, row.sha256);
    downloadCsv(downloadName(answer.fileName ?? row.fileName), bytes);
}

/** One version's files: mode, state, rows, when sent and picked up, and a Download where the claim reads them. */
const fileColumns = (businessDate, { claims, onDownload, downloading, reports }) => [
    { key: 'feed', title: 'Feed', dataIndex: 'label' },
    { key: 'mode', title: 'Mode', dataIndex: 'mode', render: (v) => <Tag>{v}</Tag> },
    { key: 'state', title: 'State', render: (_, r) => <Tag color={FILE_COLOUR[r.state]}>{titleCase(r.state)}</Tag> },
    { key: 'rows', title: 'Rows', align: 'right', render: (_, r) => (r.rowCount === null ? dash : r.rowCount) },
    {
        key: 'file',
        title: 'File',
        render: (_, r) => (r.fileName ? <Text code>{r.fileName}</Text> : dash)
    },
    { key: 'sent', title: 'Sent', render: (_, r) => (r.sentAt ? formatInstant(r.sentAt) : dash) },
    { key: 'pickedUp', title: 'Picked up', render: (_, r) => (r.pickedUpAt ? formatInstant(r.pickedUpAt) : dash) },
    {
        key: 'analytics',
        title: 'Analytics',
        render: (_, r) => {
            const words = reports ? fileAnalyticsWords(r.analytics) : null;
            return words ? <Tag color={words.colour}>{words.label}</Tag> : dash;
        }
    },
    {
        key: 'download',
        title: '',
        render: (_, r) => (r.downloadable && canDownload(r.feed, claims)
            ? <Button size="small" loading={downloading === r.feed + r.version} onClick={() => onDownload(r)}>Download</Button>
            : null)
    }
];

/** analytics' error text: its title, and its description where this reader may see it (the server's cut). */
const analyticsErrorText = (error) => `${error.title}${error.description ? ': ' + error.description : ''}`;

/**
 * A feed's failure or findings, one line each: by code and place only for a
 * reader without the register claim (the server's cut). Where reports arrive
 * (R21), analytics' error on a file it rejected, or accepted while a run it
 * should have started could not start (R24).
 */
const problemLines = (rows, reports = true) => rows.flatMap((r) => [
    ...(r.error ? [`${r.label}: ${r.error.code}${r.error.message ? ': ' + r.error.message : ''}`] : []),
    ...r.findings.slice(0, 5).map((f) => `${r.label}: ${f.rule}${f.column ? ' (' + f.column + ')' : ''}${f.rowNo ? ' at row ' + f.rowNo : ''}`),
    ...(reports && r.analytics?.status === 'REJECTED' && r.analytics.error
        ? [`${r.label}: analytics rejected the file: ${analyticsErrorText(r.analytics.error)}`]
        : []),
    ...(reports && r.analytics?.status === 'ACCEPTED' && r.analytics.error ? [`${r.label}: analytics: ${analyticsErrorText(r.analytics.error)}`] : [])
]);

/**
 * One date, expanded: per feed of the current version - mode, state, rows, sent,
 * picked up and a Download link (hidden without the claim the file needs) -
 * and the older versions folded underneath (design 9). Pure over `detail`, the
 * answer of GET /books/days/{date}/feeds.
 *
 * @param {{ businessDate: string, detail: object, onDownload: (row: object) => void, downloading?: string|null }} props
 */
export const FeedDetail = ({ businessDate, detail, onDownload, downloading = null }) => {
    const { principal } = useAccess();
    const claims = principal?.claims ?? [];
    if (!detail?.feedDay) {
        return (
            <Text type="secondary">
                {detail?.reason === 'BEFORE_START'
                    ? dayText(businessDate) + ' is before the feeds started: nothing is sent for it.'
                    : dayText(businessDate) + ' has not been swept yet: its files are built once the feeds reach it.'}
            </Text>
        );
    }
    // A stage with no analytics reports (R21) shows nothing of analytics: the API still answers AWAITING there.
    const reports = detail.analyticsReports !== false;
    const columns = fileColumns(businessDate, { claims, onDownload, downloading, reports });
    const rows = feedRows(detail);
    const older = feedVersions(detail);
    const problems = problemLines(rows, reports);
    const { feedDay } = detail;
    return (
        <div className="analytics-feeds-detail">
            <p className="business-day-meta">
                Version {feedDay.version}
                {feedDay.lastResendAt ? ` · re-sent ${formatInstant(feedDay.lastResendAt)}: ${feedDay.lastResendReason ?? ''}` : ''}
                {feedDay.wmWaivedAt ? ` · no wealth-managed holdings, confirmed ${formatInstant(feedDay.wmWaivedAt)}: ${feedDay.wmWaiveReason ?? ''}` : ''}
            </p>
            {rows.length
                ? <Table className="business-day-table" rowKey="feed" size="small" pagination={false} scroll={{ x: 'max-content' }} columns={columns} dataSource={rows} />
                : <Text type="secondary">No file has been built for this version yet.</Text>}
            {problems.length > 0 && (
                <ul className="analytics-feeds-problems">{problems.map((line) => <li key={line}>{line}</li>)}</ul>
            )}
            {reports && <AnalyticsRuns rows={rows} chainedRuns={chainedRunsOf(detail, feedDay.version)} />}
            {older.length > 0 && (
                <Collapse
                    ghost
                    size="small"
                    className="analytics-feeds-older"
                    items={older.map((v) => ({
                        key: String(v.version),
                        label: 'Version ' + v.version,
                        children: (
                            <>
                                <Table className="business-day-table" rowKey="feed" size="small" pagination={false} scroll={{ x: 'max-content' }} columns={columns} dataSource={v.rows} />
                                {reports && <AnalyticsRuns rows={v.rows} chainedRuns={chainedRunsOf(detail, v.version)} />}
                            </>
                        )
                    }))}
                />
            )}
        </div>
    );
};

/** An expanded row: loads the date's read, and downloads a file under the checks of downloadedBytes. */
const FeedDayRow = ({ businessDate, reloadKey }) => {
    const { api } = useAccess();
    const { message } = App.useApp();
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(null);
    // Each read takes the next number; only the newest one's answer or error lands. The hub calls load() and
    // drops what it returns, so an older, slower read could otherwise overwrite a newer one, or land after unmount.
    const seq = useRef(0);

    const load = useCallback(() => {
        const id = ++seq.current;
        api.books.feedDay(businessDate).then((answer) => {
            if (id !== seq.current) return;
            setDetail(answer);
            setError(null);
        }, (e) => {
            if (id === seq.current) setError(e);
        });
    }, [api, businessDate]);

    useEffect(() => {
        load();
        // A new date, a reload or an unmount: nothing still in flight lands after it.
        return () => {
            seq.current += 1;
        };
    }, [load, reloadKey]);
    // Live screens (IP-162): the sweeper and analytics' reports send `feeds`; an open date re-reads, keeping its last answer on screen.
    useLiveTopic(['feeds'], load);

    const download = async (row) => {
        setDownloading(row.feed + row.version);
        try {
            await saveFeedFile(api, businessDate, row);
            message.success('Successfully Downloaded');
        } catch (e) {
            message.error('Failed to download ' + (row.fileName ?? row.label) + ': ' + e.message);
        } finally {
            setDownloading(null);
        }
    };

    if (error && !detail) return <Alert type="error" showIcon title={'The date could not be read: ' + error.message} />;
    if (!detail) return <CenteredLoader />;
    return <FeedDetail businessDate={businessDate} detail={detail} onDownload={download} downloading={downloading} />;
};

/**
 * Re-send or Confirm no WM holdings: a reason of 3 to 500 characters on one
 * line, then the authenticator code (the FundConfig RejectModal pattern). The
 * re-send names the dates it sends again.
 */
const ActionModal = ({ title, okText, lead, dates = [], run, onClose, onDone }) => {
    const fieldId = useId();
    const [reason, setReason] = useState('');
    const [touched, setTouched] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const problem = reasonProblem(reason);

    const submit = async () => {
        if (problem) {
            setTouched(true);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await run(reason.trim());
            onDone();
        } catch (e) {
            if (!(e instanceof StepUpCancelled)) setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open
            title={title}
            onCancel={onClose}
            onOk={submit}
            okText={okText}
            okButtonProps={{ loading: busy, icon: <SafetyOutlined /> }}
            maskClosable={false}
            rootClassName="lfoc-styles-wrapper"
        >
            <p>{lead}</p>
            {dates.length > 0 && <ul className="analytics-feeds-dates">{dates.map((d) => <li key={d}>{dayLong(d)}</li>)}</ul>}
            {error && <Alert type="error" showIcon title={error} className="business-day-alert" closable={{ onClose: () => setError(null) }} />}
            <label className="business-day-field" htmlFor={fieldId}>Reason</label>
            <Input
                id={fieldId}
                value={reason}
                disabled={busy}
                maxLength={REASON_MAX}
                status={touched && problem ? 'error' : undefined}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setTouched(true)}
                onPressEnter={submit}
            />
            {touched && problem && <div className="business-day-error">{problem}</div>}
        </Modal>
    );
};

/**
 * The Analytics feeds tab (design 9): the dates not yet sent and the latest
 * sent ones, one row each - state with its reason, version, the three feeds -
 * expanding to the date's files, with Re-send and Confirm no WM holdings
 * beside the dates they apply to.
 *
 * @param {{ days: object[]|null, error?: { message: string }|null, onChanged?: () => void }} props
 *   days is GET /books/feeds' answer (null before the first read)
 */
const AnalyticsFeeds = ({ days, error = null, onChanged }) => {
    const { api, canAct } = useAccess();
    const canResend = canAct('analyticsFeeds', 'resend');
    const canWaiveWm = canAct('analyticsFeeds', 'waiveWm');
    const { message } = App.useApp();
    const { withStepUp, stepUpDialog } = useStepUp();
    const [acting, setActing] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const changed = useCallback(() => {
        setActing(null);
        setReloadKey((k) => k + 1);
        onChanged?.();
    }, [onChanged]);

    if (!days) {
        return error
            ? <Alert type="error" showIcon className="business-day-alert" title={'The analytics feeds could not be read: ' + error.message} />
            : <CenteredLoader />;
    }

    const actionOf = (kind, day) => {
        if (kind === 'resend') {
            const dates = resendDates(days, day.businessDate);
            return {
                title: 'Send the feeds of ' + dayText(day.businessDate) + ' again?',
                okText: 'Re-send',
                lead: 'These dates are built and sent again, oldest first:',
                dates,
                run: (reason) => withStepUp('FEEDS_RESEND', { businessDate: day.businessDate, reason },
                    (stepUp) => api.books.feedsResend(day.businessDate, { reason, stepUp })).then((answer) => {
                    message.success((answer?.pending?.length ?? dates.length) + ' date(s) will be sent again.');
                })
            };
        }
        return {
            title: 'Confirm that ' + dayText(day.businessDate) + ' has no wealth-managed holdings?',
            okText: 'Confirm',
            lead: 'The feeds of the day go ahead without a wealth-managed holdings file.',
            dates: [],
            run: (reason) => withStepUp('FEEDS_WM_WAIVE', { businessDate: day.businessDate, reason },
                (stepUp) => api.books.feedsWmWaive(day.businessDate, { reason, stepUp })).then(() => {
                message.success('Confirmed: the feeds of ' + dayText(day.businessDate) + ' go ahead.');
            })
        };
    };

    const columns = [
        { key: 'date', title: 'Date', width: 110, render: (_, d) => <Text>{d.businessDate}</Text> },
        {
            key: 'state',
            title: 'State',
            width: 170,
            render: (_, d) => (
                <div className="analytics-feed-day-state">
                    <Tag className="analytics-feed-day-tag" color={DAY_COLOUR[d.state]}>{titleCase(d.state)}</Tag>
                    <Text type="secondary">{feedStateWords(d)}</Text>
                </div>
            )
        },
        { key: 'version', title: 'Version', width: 90, render: (_, d) => (d.version > 0 ? d.version : dash) },
        { key: 'transactions', title: 'Transactions', width: 125, render: (_, d) => feedTag(d.feeds, 'daily-transactions') },
        { key: 'unitPrice', title: 'Unit Price', width: 125, render: (_, d) => feedTag(d.feeds, 'daily-unit-price') },
        { key: 'holdings', title: 'Client Holdings', width: 140, render: (_, d) => feedTag(d.feeds, 'daily-client-holdings') },
        {
            key: 'analytics',
            title: 'Analytics',
            width: 125,
            render: (_, d) => {
                const words = analyticsDayWords(d.analytics);
                if (!words) return dash;
                return (
                    <div className="analytics-feed-day-state">
                        <Tag className="analytics-feed-day-tag" color={words.colour} icon={d.analytics.state === 'RUNNING' ? <LoadingOutlined /> : null}>{words.label}</Tag>
                        {words.detail && <div><Text type="secondary">{words.detail}</Text></div>}
                    </div>
                );
            }
        }
    ];
    if (canResend || canWaiveWm) {
        columns.push({
            key: 'actions',
            title: '',
            width: 120,
            render: (_, d) => (
                <div className="analytics-feeds-actions">
                    {canResend && canResendDate(d) && <Button className="analytics-feed-action-button" size="small" onClick={() => setActing({ kind: 'resend', day: d })}>Re-Send</Button>}
                    {canWaiveWm && canWaiveWmDate(d) && <Button className="analytics-feed-action-button" size="small" onClick={() => setActing({ kind: 'waive', day: d })}>Confirm no WM holdings</Button>}
                </div>
            )
        });
    }
    const action = acting ? actionOf(acting.kind, acting.day) : null;

    return (
        <div className="analytics-feeds">
            {error && <Alert type="warning" showIcon className="business-day-alert" title={'Showing the last answer. ' + error.message} />}
            <Table
                className="business-day-table analytics-feed-days-table"
                rowKey="businessDate"
                size="small"
                pagination={false}
                scroll={{ x: 'max-content' }}
                columns={columns}
                dataSource={days}
                rowClassName="analytics-feed-day-row"
                expandable={{ expandedRowRender: (d) => <FeedDayRow businessDate={d.businessDate} reloadKey={reloadKey} />, rowExpandable: () => true }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No date has reached the feeds yet" /> }}
            />
            {action && (
                <ActionModal
                    key={acting.kind + acting.day.businessDate}
                    title={action.title}
                    okText={action.okText}
                    lead={action.lead}
                    dates={action.dates}
                    run={action.run}
                    onClose={() => setActing(null)}
                    onDone={changed}
                />
            )}
            {stepUpDialog}
        </div>
    );
};

export default AnalyticsFeeds;
